"use client";

import type { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import {
  useAuthenticated,
  useFloorsForOffices,
  useResourcesByIds
} from "@gettactic/hooks";
import { ArrowRight } from "lucide-react";
import React from "react";
import { LocationOnMap } from "@/features/workplace-requests/components/detail-modal/LocationOnMap";
import type { DeskMove } from "@/features/workplace-requests/lib/desk-automation";

interface DeskMoveSectionProps {
  move: DeskMove;
}

/**
 * The two ends of a relocation, side by side: the desk the person gives up and
 * the one they take, each on its own floor map. Replaces the single "Location
 * on map" card, which would only show where they are going.
 */
export const DeskMoveSection: React.FC<DeskMoveSectionProps> = ({ move }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { resources, isLoading } = useResourcesByIds([
    move.fromResourceId,
    move.toResourceId
  ]);
  const fromDesk = resources.find((r) => r.id === move.fromResourceId);
  const toDesk = resources.find((r) => r.id === move.toResourceId);

  // The two desks can sit in different offices, so both sets of floors are
  // needed to label the cards.
  const officeIds = Array.from(
    new Set(
      [fromDesk?.office_id, toDesk?.office_id].filter(
        (id): id is string => !!id
      )
    )
  );
  const floorsQuery = useFloorsForOffices(officeIds);

  const officeName = (desk: IResource): string =>
    authenticatedUser.offices?.byId?.[desk.office_id]?.name || desk.office_id;
  const floorName = (desk: IResource): string | undefined => {
    const floor = floorsQuery.data?.find((f) => f.id === desk.parent_id);
    return floor?.label || floor?.name;
  };

  return (
    <section className="mt-6">
      <h3 className="mb-2 font-medium text-neutral-500 text-xs uppercase tracking-wide">
        Desk move
      </h3>
      <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <DeskMoveCard
          label="From"
          desk={fromDesk}
          isLoading={isLoading}
          officeName={fromDesk ? officeName(fromDesk) : ""}
          floorName={fromDesk ? floorName(fromDesk) : undefined}
        />
        <ArrowRight className="hidden h-4 w-4 shrink-0 self-center text-neutral-400 md:block" />
        <DeskMoveCard
          label="To"
          desk={toDesk}
          isLoading={isLoading}
          officeName={toDesk ? officeName(toDesk) : ""}
          floorName={toDesk ? floorName(toDesk) : undefined}
          isDestination
        />
      </div>
    </section>
  );
};

interface DeskMoveCardProps {
  label: string;
  /** Undefined until the resource lookup has answered — or when it couldn't. */
  desk: IResource | undefined;
  /** Whether that lookup is still in flight. */
  isLoading: boolean;
  officeName: string;
  floorName: string | undefined;
  isDestination?: boolean;
}

const DeskMoveCard: React.FC<DeskMoveCardProps> = ({
  label,
  desk,
  isLoading,
  officeName,
  floorName,
  isDestination = false
}) => {
  const header = (
    <div className="flex items-center gap-2 border-neutral-200 border-b px-4 py-2.5">
      <span
        className={cn(
          "shrink-0 rounded-md px-1.5 py-0.5 font-semibold text-xs uppercase tracking-wide",
          isDestination
            ? "bg-primary/10 text-primary"
            : "bg-neutral-100 text-neutral-600"
        )}
      >
        {label}
      </span>
      <span className="truncate font-semibold text-neutral-900 text-sm">
        {desk?.name ?? (isLoading ? "…" : "Unknown desk")}
      </span>
    </div>
  );

  // Until the resource answers, nothing places the map: not the office, not
  // the floor. The card keeps its shape so the pair doesn't jump on load. A
  // desk that answered without a floor isn't on any map, and one the lookup
  // couldn't return — deleted, or in an office the viewer can't read — isn't
  // coming; both say so rather than loading forever.
  if (!desk?.parent_id) {
    const message = desk
      ? "Not placed on a map"
      : isLoading
        ? "Loading map…"
        : "Desk not found";
    return (
      <div className="overflow-hidden rounded-xl border border-neutral-200">
        {header}
        <div className="flex h-[202px] items-center justify-center bg-neutral-50 text-neutral-400 text-sm">
          {message}
        </div>
      </div>
    );
  }

  return (
    <LocationOnMap
      header={header}
      officeId={desk.office_id}
      floorId={desk.parent_id}
      resourceId={desk.id}
      officeName={officeName}
      floorName={floorName}
    />
  );
};
